import { FC } from 'react';
import { Avatar, Button, Checkbox, Col, Row, Tag } from 'antd';
import { capitalCase } from 'change-case';
import { BsLinkedin } from 'react-icons/bs';
import { FaFacebook } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { SiCrunchbase } from 'react-icons/si';
import { TbBuilding, TbFlag, TbLink } from 'react-icons/tb';
import { Link, useNavigate } from 'react-router-dom';
import { normalizeURL, withoutTrailingSlash } from 'ufo';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useImportedEntityDetailPrefetch, useSearchCompanyDetailPrefetch } from '@/queries';
import {
    FundIcon,
    VirtualizedList as List,
    MiddleDot,
    ResourceTagGroup,
    SmallTooltip,
    TickerExchangeTag,
} from '@/shared';
import {
    getCompanySearchResultDetailRoute,
    isCompanySearchResultFundLike,
} from '@/shared/components/company-search/utils';
import { useInvestmentStore } from '@/stores';
import { Investment, Tag as WorkspaceTag } from '@/types';
import { dayjs, ellipsisStyling, resolveCorporateLogo } from '@/utilities';

import { getCompanyResultColumnFilter } from '../../../../../../../../helpers';
import { companySearchResultsGridCellStyle, getCompanySearchResultsGridStyle } from '../layout';
import CompanyRowActions from './CompanyRowActions';

interface CompanyListItemProps {
    data: any;
    resultsSelected: any;
    setResultsSelected: any;
    listColumns: any;
    setCheckAll: any;
    setIndeterminate: any;
    tags?: WorkspaceTag[];
    tagsLoading?: boolean;
    resultsCount: number;
    selected?: boolean;
    selectionId?: string;
    onSelectedChange?: (checked: boolean) => void;
    onApplyColumnFilter?: (column: string, result: unknown) => void;
}

const CompanyListItem: FC<CompanyListItemProps> = ({
    data,
    resultsSelected,
    setResultsSelected,
    listColumns,
    setCheckAll,
    setIndeterminate,
    resultsCount,
    tags,
    tagsLoading,
    selected,
    selectionId,
    onSelectedChange,
    onApplyColumnFilter,
}): JSX.Element => {
    const { resolvedThemeName, token } = useAntDesignTheme();

    const isImportedEntity = data?.resultType === 'importedEntity';
    const isNylasCompany = data?.resultType === 'nylasCompany';
    const itemId = selectionId ?? data?.entityId ?? data?.symbol;
    const key = itemId ?? data?.companyName;
    const hasReferenceLinks = Boolean(
        data?.enrichment?.website ||
        (!isImportedEntity &&
            (data?.enrichment?.clearbit?.linkedin?.handle ||
                data?.enrichment?.clearbit?.twitter?.handle ||
                data?.enrichment?.clearbit?.crunchbase?.handle ||
                data?.enrichment?.clearbit?.facebook?.handle)),
    );

    const columnToCompanyEndpointMap = {
        // FMP
        symbol: <>{data?.enrichment?.symbol ? data?.enrichment?.symbol : 'N/A'}</>,
        price: <>{data?.enrichment?.price ? `$${Number(data?.enrichment?.price).toLocaleString()}` : 'N/A'}</>,
        beta: <>{data?.enrichment?.beta ? data?.enrichment?.beta : 'N/A'}</>,
        volAvg: <>{data?.enrichment?.volAvg ? Number(data?.enrichment?.volAvg)?.toLocaleString() : 'N/A'}</>,
        lastDiv: <>{data?.enrichment?.lastDiv ? `$${Number(data?.enrichment?.lastDiv).toLocaleString()}` : 'N/A'}</>,
        companyName: <>{data?.enrichment?.companyName ? data?.enrichment?.companyName : 'N/A'}</>,
        mktCap: <>{data?.enrichment?.mktCap ? `$${Number(data?.enrichment?.mktCap).toLocaleString()}` : 'N/A'}</>,
        cik: <>{data?.enrichment?.cik ? `${Number(data?.enrichment?.cik)}` : 'N/A'}</>,
        isin: <>{data?.enrichment?.isin ? `${Number(data?.enrichment?.isin)}` : 'N/A'}</>,
        cusip: <>{data?.enrichment?.cusip ? `${Number(data?.enrichment?.cusip)}` : 'N/A'}</>,
        exchange: <>{data?.enrichment?.exchange ? `${data?.enrichment?.exchange}` : 'N/A'}</>,
        // exchange: <>{data?.enrichment?.exchange ? `${data?.enrichment?.exchange}` : 'N/A'}</>,
        industry: (
            <Tag
                style={{
                    margin: 0,
                    ...ellipsisStyling,
                    overflow: 'hidden',
                    verticalAlign: '-0.25rem',
                }}
            >
                {data?.enrichment?.industry ? data?.enrichment?.industry : 'N/A'}
            </Tag>
        ),
        website: (
            <>
                {data?.enrichment?.website ? (
                    <a href={data?.enrichment?.website} target='_blank' rel='noopener noreferrer'>
                        {data?.enrichment?.website}
                    </a>
                ) : (
                    'N/A'
                )}
            </>
        ),
        description:
            data?.enrichment?.description && (data?.enrichment?.description ? data?.enrichment?.description : 'N/A'),
        ceo: <>{data?.enrichment?.ceo ? data?.enrichment?.ceo : 'N/A'}</>,
        sector: (
            <Tag
                style={{
                    margin: 0,
                    ...ellipsisStyling,
                    overflow: 'hidden',
                    verticalAlign: '-0.25rem',
                }}
            >
                {data?.enrichment?.sector ? data?.enrichment?.sector : 'N/A'}
            </Tag>
        ),
        fullTimeEmployees: (
            <>
                {data?.enrichment?.fullTimeEmployees
                    ? `~${Number(data?.enrichment?.fullTimeEmployees).toLocaleString()}`
                    : 'N/A'}
            </>
        ),
        phone: <>{data?.enrichment?.phone ? <>{data?.enrichment?.phone}</> : 'N/A'}</>,
        address: <>{data?.enrichment?.address ? data?.enrichment?.address : 'N/A'}</>,
        city: <>{data?.enrichment?.city ? data?.enrichment?.city : 'N/A'}</>,
        state: <>{data?.enrichment?.state ? data?.enrichment?.state : 'N/A'}</>,
        zip: <>{data?.enrichment?.zip ? data?.enrichment?.zip : 'N/A'}</>,
        ipoDate: <>{data?.enrichment?.ipoDate ? dayjs(data?.enrichment?.ipoDate).format('LL') : 'N/A'}</>,
        country: (
            <Tag
                style={{
                    margin: 0,
                    ...ellipsisStyling,
                    overflow: 'hidden',
                    verticalAlign: '-0.25rem',
                }}
            >
                {data?.enrichment?.clearbit?.geo?.countryCode ? (
                    <img
                        height={16}
                        width={24}
                        src={
                            data?.enrichment?.clearbit?.geo?.countryCode &&
                            `https://flagcdn.com/${data?.enrichment?.clearbit?.geo?.countryCode?.toLowerCase()}.svg`
                        }
                        style={{
                            marginRight: '0.5rem',
                            verticalAlign: '-0.2rem',
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                        }}
                    />
                ) : (
                    <TbFlag style={{ verticalAlign: '-0.1rem', marginRight: '0.325rem' }} />
                )}
                {data?.enrichment?.clearbit?.geo?.country ? data?.enrichment?.clearbit?.geo?.country : 'N/A'}
            </Tag>
        ),
        // Clearbit
        industryGroup: (
            <Tag>
                {data?.enrichment?.clearbit?.category?.industryGroup
                    ? data?.enrichment?.clearbit?.category?.industryGroup
                    : 'N/A'}
            </Tag>
        ),
        subIndustry: (
            <Tag>
                {data?.enrichment?.clearbit?.category?.subIndustry
                    ? data?.enrichment?.clearbit?.category?.subIndustry
                    : 'N/A'}
            </Tag>
        ),
        gicsCode: (
            <>
                {data?.enrichment?.clearbit?.category?.gicsCode
                    ? data?.enrichment?.clearbit?.category?.gicsCode
                    : 'N/A'}
            </>
        ),
        naicsCode: (
            <>
                {data?.enrichment?.clearbit?.category?.naicsCode
                    ? data?.enrichment?.clearbit?.category?.naicsCode
                    : 'N/A'}
            </>
        ),
        tags: (
            <>
                {data?.enrichment?.clearbit?.tags?.length !== 0
                    ? data?.enrichment?.clearbit?.tags?.map((t: string) => {
                          return <Tag style={{}}>{t}</Tag>;
                      })
                    : 'N/A'}
            </>
        ),
        foundedYear: <>{data?.enrichment?.clearbit?.foundedYear ? data?.enrichment?.clearbit?.foundedYear : 'N/A'}</>,
        timeZone: <>{data?.enrichment?.clearbit?.timeZone ? data?.enrichment?.clearbit?.timeZone : 'N/A'}</>,
        facebook: (
            <>
                {data?.enrichment?.clearbit?.facebook?.handle ? (
                    <a
                        href={`https://facebook.com/${data?.enrichment?.clearbit?.facebook?.handle}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        facebook.com/{data?.enrichment?.clearbit?.facebook?.handle}
                    </a>
                ) : (
                    'N/A'
                )}
            </>
        ),
        linkedin: (
            <>
                {data?.enrichment?.clearbit?.linkedin?.handle ? (
                    <a
                        href={`https://linkedin.com/${data?.enrichment?.clearbit?.linkedin?.handle}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        linkedin.com/{data?.enrichment?.clearbit?.linkedin?.handle}
                    </a>
                ) : (
                    'N/A'
                )}
            </>
        ),
        twitter: (
            <>
                {data?.enrichment?.clearbit?.twitter?.handle ? (
                    <a
                        href={`https://twitter.com/${data?.enrichment?.clearbit?.twitter?.handle}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        twitter.com/{data?.enrichment?.clearbit?.twitter?.handle}
                    </a>
                ) : (
                    'N/A'
                )}
            </>
        ),
        crunchbase: (
            <>
                {data?.enrichment?.clearbit?.crunchbase?.handle ? (
                    <a
                        href={`https://crunchbase.com/${data?.enrichment?.clearbit?.crunchbase?.handle}`}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        crunchbase.com/{data?.enrichment?.clearbit?.crunchbase?.handle}
                    </a>
                ) : (
                    'N/A'
                )}
            </>
        ),
        trafficRank: (
            <>
                {data?.enrichment?.clearbit?.metrics?.trafficRank ? (
                    <Tag>{capitalCase(data?.enrichment?.clearbit?.metrics?.trafficRank?.replace('_', ' '))}</Tag>
                ) : (
                    'N/A'
                )}
            </>
        ),
        raised: (
            <>
                {data?.enrichment?.clearbit?.metrics?.raised ? (
                    <>${Number(data?.enrichment?.clearbit?.metrics?.raised).toLocaleString()}</>
                ) : (
                    'N/A'
                )}
            </>
        ),
        fiscalYearEnd: (
            <>
                {data?.enrichment?.clearbit?.metrics?.fiscalYearEnd ? (
                    <>
                        {dayjs()
                            .month(data?.enrichment?.clearbit?.metrics?.fiscalYearEnd - 1)
                            .format('MMMM')}
                    </>
                ) : (
                    'N/A'
                )}
            </>
        ),
        tech: (
            <>
                {data?.enrichment?.clearbit?.tech?.length !== 0
                    ? data?.enrichment?.clearbit?.tech?.map((t: string) => {
                          return <Tag style={{}}>{capitalCase(t?.replace('_', ' '))}</Tag>;
                      })
                    : 'N/A'}
            </>
        ),
        techCategories: (
            <>
                {data?.enrichment?.clearbit?.techCategories?.length !== 0
                    ? data?.enrichment?.clearbit?.techCategories?.map((t: string) => {
                          return <Tag style={{}}>{capitalCase(t?.replace('_', ' '))}</Tag>;
                      })
                    : 'N/A'}
            </>
        ),
    } as any;

    const navigate = useNavigate();

    const { createInvestment } = useInvestmentStore();
    const isFundLike = isCompanySearchResultFundLike(data);
    const detailRoute = getCompanySearchResultDetailRoute(data);
    const prefetchSearchCompanyDetail = useSearchCompanyDetailPrefetch(isNylasCompany ? undefined : data?.symbol);
    const prefetchImportedEntityDetail = useImportedEntityDetailPrefetch(data?.entityId);
    const detailPrefetchProps = isNylasCompany
        ? {}
        : isImportedEntity
          ? prefetchImportedEntityDetail
          : prefetchSearchCompanyDetail;

    const handleCreateInvestment = async () => {
        const headquarters =
            data?.enrichment?.country === 'US'
                ? // ? `${data?.enrichment?.city}, ${stateConverter(data?.enrichment?.state, 'abbr')}`
                  `${data?.enrichment?.city}`
                : data?.enrichment?.country;

        const address =
            data?.enrichment?.address && data?.enrichment?.address2
                ? `${data?.enrichment?.address}, ${data?.enrichment?.address2}`
                : data?.enrichment?.address
                  ? data?.enrichment?.address
                  : '-';

        const companyName = data?.enrichment?.companyName ?? data?.companyName ?? data?.name;
        const payload = {
            name: companyName,
            editedName: companyName,
            description: data?.enrichment?.description,
            tickerSymbol: isNylasCompany ? undefined : data?.symbol,
            marketType: isNylasCompany
                ? 'Private'
                : data?.enrichment?.clearbit?.type
                  ? capitalCase(data?.enrichment?.clearbit?.type)
                  : 'Public',
            financingType: 'Equity',
            issuance: data?.enrichment?.issueType,
            cik: data?.enrichment?.cik,
            lei: data?.enrichment?.lei, // ?
            figi: data?.enrichment?.figi, // ?
            exchange: isNylasCompany ? undefined : (data.enrichment?.exchange ?? data.exchange),
            foundingDate: data?.enrichment?.clearbit?.foundedYear
                ? String(data?.enrichment?.clearbit?.foundedYear)
                : '-',
            ipoDate: data?.enrichment?.ipoDate ? String(data?.enrichment?.ipoDate) : '-',
            address,
            headquarters,
            country: data?.enrichment?.country,
            industry: data?.enrichment?.industry
                ? data?.enrichment?.industry
                : data?.enrichment?.clearbit?.category?.industry,
            sector: data?.enrichment?.sector,
            ceo: data?.enrichment?.ceo,
            employees: data?.enrichment?.fullTimeEmployees,
            website: data?.enrichment?.website
                ? normalizeURL(withoutTrailingSlash(data.enrichment.website, true))
                : undefined,
            tags: data?.enrichment?.clearbit?.tags,
            bannerImage: '',
            type: 'Personal',
            clearbit: {
                ...data?.enrichment?.clearbit,
                fmp: {
                    exchange: isNylasCompany ? undefined : (data.enrichment?.exchange ?? data.exchange),
                    isAdr: data?.enrichment?.isAdr,
                    isEtf: data?.enrichment?.isEtf,
                    isFund: data?.enrichment?.isFund,
                    symbol: data?.symbol,
                },
            },
        } as Partial<Investment>;

        createInvestment(payload).then((i) => {
            navigate(`/investments/${i?.id}`);
        });
    };

    const handleItemChange = (item: any, checked: boolean) => {
        if (onSelectedChange) {
            onSelectedChange(checked);
            return;
        }

        const newSelectedItems = new Set(resultsSelected);
        const selectedItemId = selectionId ?? item.entityId ?? item.symbol;

        if (checked) {
            newSelectedItems.add(selectedItemId);
        } else {
            newSelectedItems.delete(selectedItemId);
        }

        setResultsSelected(newSelectedItems);

        // Update "Select All" and "Indeterminate" states
        setCheckAll(newSelectedItems.size === resultsCount);
        setIndeterminate(newSelectedItems.size > 0 && newSelectedItems.size < resultsCount);
    };

    return (
        <List.Item key={key}>
            <div style={getCompanySearchResultsGridStyle(listColumns)}>
                <div style={companySearchResultsGridCellStyle}>
                    <Row align={'middle'} wrap={false}>
                        <Col data-selection-control='true' flex='none'>
                            <Checkbox
                                checked={selected ?? resultsSelected.has(itemId)}
                                onChange={(e) => handleItemChange(data, e.target.checked)}
                            />
                        </Col>
                        <Col flex='none' style={{ padding: '0 1rem' }}>
                            <div style={{ display: 'inline-block' }}>
                                <Avatar
                                    // src={data?.logo !== '' ? data?.logo : undefined}
                                    src={resolveCorporateLogo(
                                        data?.enrichment?.website ?? data?.website,
                                        data?.logoUrl ??
                                            data?.enrichment?.logoUrl ??
                                            data?.enrichment?.image ??
                                            data?.image,
                                        {
                                            theme: resolvedThemeName === 'dark' ? 'dark' : 'light',
                                        },
                                        {
                                            name: data?.enrichment?.companyName ?? data?.companyName ?? data?.name,
                                            ticker: data?.enrichment?.symbol ?? data?.symbol,
                                        },
                                    )}
                                    icon={
                                        isFundLike ? (
                                            <FundIcon
                                                style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}
                                            />
                                        ) : (
                                            <TbBuilding
                                                style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}
                                            />
                                        )
                                    }
                                    size={'large'}
                                    style={{
                                        verticalAlign: '-1.5rem',
                                        border: `1px solid ${token.colorBorder}`,

                                        backgroundColor: token.colorBgBase,
                                    }}
                                    shape='square'
                                />
                            </div>
                        </Col>
                        <Col flex='auto'>
                            <div style={{ verticalAlign: '-0.75rem', ...ellipsisStyling }}>
                                <div
                                    style={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        gap: '0.5rem',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* <QuickPeakDropdown data={data}> */}
                                    <span
                                        style={{
                                            display: 'block',
                                            flex: '1 1 auto',
                                            fontWeight: 500,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {detailRoute ? (
                                            <Link
                                                style={{
                                                    color: 'inherit',
                                                    display: 'block',
                                                    overflow: 'hidden',
                                                    textDecoration: 'underline',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={data?.name}
                                                to={detailRoute}
                                                {...detailPrefetchProps}
                                            >
                                                {data?.name}
                                            </Link>
                                        ) : (
                                            <span title={data?.name}>{data?.name}</span>
                                        )}
                                    </span>
                                    {/* </QuickPeakDropdown> */}
                                    {(data?.enrichment?.symbol ?? data?.symbol) && (
                                        <TickerExchangeTag
                                            companyName={
                                                data?.enrichment?.companyName ?? data?.companyName ?? data?.name
                                            }
                                            compact
                                            exchange={data?.enrichment?.exchange ?? data?.exchange}
                                            styles={{ flex: '0 0 auto', margin: 0, minWidth: 'max-content' }}
                                            symbol={data?.enrichment?.symbol ?? data?.symbol}
                                            website={data?.enrichment?.website ?? data?.website}
                                        />
                                    )}
                                    {isImportedEntity ? (
                                        <>
                                            <Tag style={{ borderColor: 'transparent', flex: '0 0 auto', margin: 0 }}>
                                                Imported
                                            </Tag>
                                            <Tag style={{ borderColor: 'transparent', flex: '0 0 auto', margin: 0 }}>
                                                {data?.entityType}
                                            </Tag>
                                        </>
                                    ) : isNylasCompany ? (
                                        <>
                                            <Tag style={{ borderColor: 'transparent', flex: '0 0 auto', margin: 0 }}>
                                                Connected
                                            </Tag>
                                            <Tag style={{ borderColor: 'transparent', flex: '0 0 auto', margin: 0 }}>
                                                Private
                                            </Tag>
                                        </>
                                    ) : null}
                                    {isFundLike && (
                                        <Tag
                                            style={{
                                                borderColor: 'transparent',
                                                flex: '0 0 auto',
                                                margin: 0,
                                            }}
                                        >
                                            {data?.enrichment?.isEtf ? 'ETF' : 'Fund'}
                                        </Tag>
                                    )}
                                </div>
                                <div
                                    style={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        minWidth: 0,
                                        ...ellipsisStyling,
                                    }}
                                >
                                    <span style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }}>
                                        {data?.enrichment?.website && (
                                            <a
                                                href={data?.enrichment?.website}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                style={{ color: 'inherit' }}
                                            >
                                                <TbLink size='0.875rem' />
                                            </a>
                                        )}
                                        {!isImportedEntity && data?.enrichment?.clearbit?.linkedin?.handle && (
                                            <a
                                                href={`https://linkedin.com/${data?.enrichment?.clearbit?.linkedin?.handle}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                style={{ color: 'inherit' }}
                                            >
                                                <BsLinkedin
                                                    size='0.875rem'
                                                    style={{
                                                        marginLeft: '0.5rem',
                                                        borderRadius: token.borderRadius,
                                                    }}
                                                />
                                            </a>
                                        )}
                                        {!isImportedEntity && data?.enrichment?.clearbit?.twitter?.handle && (
                                            <a
                                                href={`http://x.com/${data?.enrichment?.clearbit?.twitter?.handle}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                style={{ color: 'inherit' }}
                                            >
                                                <FaSquareXTwitter
                                                    size='1rem'
                                                    style={{
                                                        marginLeft: '0.5rem',
                                                        verticalAlign: '-0.05rem',
                                                    }}
                                                />
                                            </a>
                                        )}
                                        {!isImportedEntity && data?.enrichment?.clearbit?.crunchbase?.handle && (
                                            <a
                                                href={`https://crunchbase.com/${data?.enrichment?.clearbit?.crunchbase?.handle}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                style={{ color: 'inherit' }}
                                            >
                                                <SiCrunchbase
                                                    size='0.875rem'
                                                    style={{
                                                        marginLeft: '0.5rem',
                                                        borderRadius: token.borderRadius,
                                                    }}
                                                />
                                            </a>
                                        )}
                                        {!isImportedEntity && data?.enrichment?.clearbit?.facebook?.handle && (
                                            <a
                                                href={`https://facebook.com/${data?.enrichment?.clearbit?.facebook?.handle}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                style={{ marginLeft: '0.5rem', color: 'inherit' }}
                                            >
                                                <FaFacebook size='0.875rem' />
                                            </a>
                                        )}
                                    </span>
                                    {hasReferenceLinks && (tagsLoading || tags?.length) ? (
                                        <MiddleDot horizontalMargin='0.375rem' />
                                    ) : null}
                                    <ResourceTagGroup loading={tagsLoading} max={3} searchTab='companies' tags={tags} />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                {/* <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>

                </div> */}
                {listColumns.map((col: string) => {
                    const columnFilter = getCompanyResultColumnFilter(col, data);
                    const content = col && columnToCompanyEndpointMap[col];

                    return (
                        <div
                            key={col}
                            style={{
                                ...companySearchResultsGridCellStyle,
                                textAlign: 'right',
                                ...ellipsisStyling,
                            }}
                        >
                            {columnFilter && onApplyColumnFilter ? (
                                <SmallTooltip title={`Filter by ${columnFilter.label}`}>
                                    <Button
                                        aria-label={`Filter results by ${columnFilter.label}`}
                                        size='small'
                                        type='text'
                                        onClick={() => onApplyColumnFilter(col, data)}
                                        style={{
                                            color: 'inherit',
                                            height: 'auto',
                                            maxWidth: '100%',
                                            textDecoration: 'underline',
                                        }}
                                    >
                                        <span style={{ ...ellipsisStyling, maxWidth: '100%' }}>{content}</span>
                                    </Button>
                                </SmallTooltip>
                            ) : (
                                content
                            )}
                        </div>
                    );
                })}
                <div style={{ ...companySearchResultsGridCellStyle, textAlign: 'right' }}>
                    <CompanyRowActions
                        detailRoute={detailRoute}
                        result={data}
                        selectionId={itemId}
                        onCreateInvestment={handleCreateInvestment}
                    />
                </div>
            </div>
        </List.Item>
    );
};

export default CompanyListItem;
